import { color, defineDirective, getDistinctValues, groupBy, IOmniaShell, IUibModalScope } from "../omnia"
import { IDatalayersService, IFieldsService } from "../types"
import { YieldScalesContext } from "./context"
import { updateScalesOnYearChanged } from "./utils"
import { CropVM } from "./vm/crop"
import { GroupVM } from "./vm/group"
import { CropScalesVM } from "./vm/scale"

/// <reference path="vm/crop.ts" />
/// <reference path="vm/scale.ts" />
/// <reference path="vm/group.ts" />
/// <reference path="context.ts" />

import m = omnia.models;

export class YieldScalesCtrl {

    static create(): ng.IDirective {

        return defineDirective(YieldScalesCtrl, "app/datalayers/yield-scales/yield-scales.html", {
            context: "="
        });

    }

    static $inject = ["$scope", "OmniaShell", "OmniaDatalayersService", "OmniaFieldsService"];

    private $scope: IUibModalScope;

    private ready: boolean;

    private colorPickerOptions = {
        closeWhenSelected: true
    };

    private _selectedCrop: CropVM;
    private croppingCropsIds: number[];

    public context: YieldScalesContext;

    public groups: GroupVM[]
    public scales: CropScalesVM[];
    public selectedScale: CropScalesVM;
    public crops: CropVM[];

    public isEditing: boolean;

    constructor($scope: ng.IScope,
        private shell: IOmniaShell,
        private svc: IDatalayersService,
        private fieldsSvc: IFieldsService,
        private readonly session = shell.getSession()) {

        this.$scope = <IUibModalScope>$scope.$parent;

    }

    $onInit() {

        this.ready = false;

        this.fieldsSvc.refresh().then(() => {

            this.crops = this.fieldsSvc.crops.filter(c => c.TargetYieldReqd || c.Grass).map(c => new CropVM(c));

            this.croppingCropsIds = getDistinctValues(this.fieldsSvc.cropping.filter(c => c.currentYear && !c.uncropped).map(c => c.cropId), i => i);

            const scales = this.svc.getCropYieldScales()
            this.scales = scales.map(s => new CropScalesVM(this.crops.find(c => c.id === s.CropId), s));

            this.updateGroups()

            this.context.getEditedScales = () => this.getEditedScales();
            this.context.getRemovedScalesIds = () => this.getRemovedScalesIds();
            this.context.scalesChanged = () => this.scales.filter(s => s.edited || s.removed).length > 0;
            this.context.scaleValid = () => this.canCommit();

            this.context.isEditing = () => this.isEditing
            this.context.endEdition = () => this.endEdition()

            this.context.createScale = () => this.createScale()

            this.ready = true;

        });
    }

    get year(): number {
        return this.session.yearId;
    }

    get selectedCrop(): CropVM {
        return this._selectedCrop;
    }

    set selectedCrop(value: CropVM) {
        this._selectedCrop = value;
    }

    get selectedScaleYears() {
        return this.selectedScale && this.selectedScale.years
    }

    set selectedScaleYears(years) {
        this.selectedScale.years = years
        updateScalesOnYearChanged(this.scales, this.selectedScale)
    }

    createScale() {

        this.shell.showComponentAsDialog("omnia-maplayers-edit-yield-scales-create-dialog", {}, { backdrop: 'static' }).then(r => {

            const scale = <m.ICropYieldScale>r

            if (scale) {

                const scaleVM = new CropScalesVM(this.crops.find(c => c.id === scale.CropId), scale);
                scaleVM.isNew = true;
                this.scales.push(scaleVM);
                this.updateGroups()

                updateScalesOnYearChanged(this.scales, scaleVM)

                this.editScale(scaleVM);
            }

        });
    }

    editScale(scale: CropScalesVM) {
        this.selectedCrop = scale ? scale.crop : undefined;
        this.selectedScale = scale;
        this.isEditing = true;
    }

    endEdition() {
        this.isEditing = false;
        this.selectedScale = undefined;
        this.selectedCrop = undefined;
    }

    getMapStyleCssColor(i: number) {
        return color.colorToCss(this.selectedScale.scale.MapStyle[i]);
    }

    canCommit(): boolean {
        return !this.selectedScale || (this.selectedScale && this.selectedScale.isValid());
    }

    getEditedScales(): m.ICropYieldScale[] {
        return this.scales.filter(s => s.edited && !s.removed).map(s => s.scale);
    }

    getRemovedScalesIds(): number[] {
        return this.scales.filter(s => s.removed && !s.isNew).map(s => s.scale.CropYieldScaleId);
    }

    addInterval() {
        this.selectedScale.addInterval()
    }

    removeInterval(index: number) {
        this.selectedScale.removeInterval(index)
    }

    reverse() {
        this.selectedScale.scale.MapStyle = this.selectedScale.scale.MapStyle.reverse();
    }

    deleteScale() {

        const scale = this.selectedScale
        if (!scale) return

        if (scale.isNew) {
            this.scales.remove(s => s == scale)
            this.updateGroups()
        }
        else {
            this.selectedScale.removed = !this.selectedScale.removed;
        }

        this.endEdition()

    }

    private updateGroups() {

        this.groups = groupBy(this.scales, s => s.crop, c => c.id)
            .map(g => new GroupVM(g.key, g.values))
            .sort((g1, g2) => g1.name.localeCompare(g2.name))

    }

}
